import { format } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { toast } from "./ui/use-toast"
import { base64PDFToBlobUrl } from "../lib/utils"
import { fetchHealthRecordsByBookingId, rescheduleAppointment, cancelAppointment, patientConfirmsPayment, fetchPaymentConfirmDetails } from "../services/patients"
import { useDoctorSlots } from "../lib/hooks/doctors/use-doctor-slots"
import { useNavigate } from "react-router-dom"
import AppointmentHeader from "./confirmation-page/AppointmentHeader"
import DoctorDetailsCard from "./confirmation-page/DoctorDetailsCard"
import AppointmentTimeBox from "./confirmation-page/AppointmentTimeBox"
import CancelAppointmentModal from "./confirmation-page/CancelAppointmentModal"
import RescheduleModal from "./confirmation-page/RescheduleModal"
import TeleConsultationSection from "./confirmation-page/TeleConsultationSection"
import SymptomsCard from "./confirmation-page/SymptomsCard"
import ClinicDetailsCard from "./confirmation-page/ClinicDetailsCard"
import HealthRecordsList from "./confirmation-page/HealthRecordsList"
import PaymentConfirmation from "./confirmation-page/PaymentConfirmation"

const MAX_SLOTS = 6
const today = format(new Date(), "dd-MM-yyyy")

function AppointmentDetails({
  doctor,
  data,
  branchDetails,
  paymentDetailsData,
  professionalId,
}: {
  doctor:
  | {
    fullName: string
    profilePhoto?: string
    specialities?: string[]
    subDistrictOrCity?: string
    fee?: string
    clinicName?: string
    state?: string
  }
  | undefined
  data: {
    slotDate?: Date
    dayOfWeek?: string
    slotTiming?: string
    symptoms?: string[]
    patient?: { name: string }
    bookingId?: number
    appointmentType?: string
    status?: string
    patientCode?: string
    patientId?: number
  } | null

  branchDetails?: {
    branchId: number
    clinicName: string
    subDistrictOrCity: string
    state: string
    address: string
    pincode: string
    latitude: string
    longitude: string
  }[]

  paymentDetailsData?: {
    upiId: string | null;
    qrUploaded: boolean;
    base64File: string;
  } | null
  professionalId: number | undefined; 
}) {
  const [healthRecords, setHealthRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<number | undefined>()
  const [selectedDate, setSelectedDate] = useState(today)
  const [tempSelectedSlot, setTempSelectedSlot] = useState<{ from: string; to: string } | null>(null)
  const [slotInfo, setSlotInfo] = useState("")
  const [timeInfo, setTimeInfo] = useState("")
  const [currentBookingSlot, setCurrentBookingSlot] = useState<{ from: string; to: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false)

  // New state variables for reschedule modal
  const [originalSlot, setOriginalSlot] = useState<{ from: string; to: string } | null>(null)
  const [originalDate, setOriginalDate] = useState<string>("")
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)

  const [paymentChecked, setPaymentChecked] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");
  const [loadingPaymentSubmit, setLoadingPaymentSubmit] = useState(false);

  const navigate = useNavigate()

  const parseSlotInfo = (slotInfoString: string) => {
    if (!slotInfoString || slotInfoString === "Slot information unavailable") {
      console.log("🚫 slotInfoString is empty or unavailable:", slotInfoString);
      return null;
    }

    // slotInfo format: "08:00 am - 08:15 am, Thursday, 17 Jul 2025"
    //console.log("🔍 Parsing slotInfo:", slotInfoString);
    const parts = slotInfoString.split(', ');
    // console.log("🔍 Split parts:", parts);

    if (parts.length < 3) {
      // console.log("🚫 Invalid slotInfo format, parts length < 3:", parts);
      return null;
    }

    const datePart = parts[2]; // "17 Jul 2025"
    const timePart = parts[0]; // "08:00 am - 08:15 am"
    //console.log("🔍 Date part:", datePart, "Time part:", timePart);

    // Convert date to dd-MM-yyyy format
    try {
      const parsedDate = new Date(datePart);
      // console.log("🔍 Parsed date:", parsedDate);
      if (isNaN(parsedDate.getTime())) {
        //console.log("🚫 Invalid date format:", datePart);
        return null;
      }
      const formattedDate = format(parsedDate, "dd-MM-yyyy");
      //console.log("🔍 Formatted date:", formattedDate);

      // Parse time slot
      if (timePart.includes(' - ')) {
        const [from, to] = timePart.split(' - ');
        //console.log("🔍 Parsed time slot: from", from.trim(), "to", to.trim());
        return {
          date: formattedDate,
          slot: { from: from.trim(), to: to.trim() }
        };
      } else {
        //console.log("🚫 Invalid time format:", timePart);
        return null;
      }
    } catch (error) {
      console.error("🚫 Error parsing slot info:", error);
      return null;
    }
  };

  useEffect(() => {
    //console.log("🔄 Setting up original booking details", { data })

    if (data?.slotDate && data?.dayOfWeek && data?.slotTiming) {
      const formattedDate = format(data.slotDate, "d MMM yyyy")
      // const info = `${formattedDate}, ${data.dayOfWeek}, ${data.slotTiming}`
      const info = `${data.slotTiming}, ${data.dayOfWeek}, ${formattedDate}`
      const time = `on ${data.dayOfWeek}, ${formattedDate} at ${data.slotTiming}`
      setSlotInfo(info)

      setTimeInfo(time)
      // Set original booking details
      const originalFormattedDate = format(data.slotDate, "dd-MM-yyyy")
      setOriginalDate(originalFormattedDate)
      //console.log("📅 Original date set:", originalFormattedDate)

      // Parse the original slot timing
      if (data.slotTiming && data.slotTiming.includes(' - ')) {
        const [from, to] = data.slotTiming.split(' - ')
        const originalSlotData = { from: from.trim(), to: to.trim() }
        setOriginalSlot(originalSlotData)
        //console.log("⏰ Original slot set:", originalSlotData)
      } else {
        //console.log("🚫 Could not parse original slot timing:", data.slotTiming)
      }
    } else {
      setSlotInfo("Slot information unavailable")
    }
  }, [data])

  const branch = branchDetails?.[0]

  const { slots = {}, isLoading: isSlotsLoading, isFetched: isSlotsFetched } = useDoctorSlots(professionalId?.toString(), selectedBranch)

  const selectedDateSlots = useMemo(() => {
    const slotsForDate = slots[selectedDate] ?? []
    //console.log("📊 Slots for selected date", selectedDate, ":", slotsForDate)
    return slotsForDate
  }, [slots, selectedDate])

  const [paginatedSlots, setPaginatedSlots] = useState<typeof selectedDateSlots>([])
  const [currentActiveSlots, setCurrentActiveSlots] = useState<typeof selectedDateSlots>([]);

  useEffect(() => {
    const getHealthRecords = async () => {
      if (!data?.bookingId || !data?.patient?.name) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch by bookingId
        const recordsByBookingId = await fetchHealthRecordsByBookingId(data.bookingId);
        // Optionally fetch by name and mobile if mobile is availabley
        // Combine or choose records based on your needs
        setHealthRecords(recordsByBookingId);
      } catch (err: any) {
        //setError(err.message || "Failed to fetch health records. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    getHealthRecords();
  }, [data?.bookingId, data?.patient?.name]);

  function handleCancelAppointment() {
    if (!data?.bookingId) {
      return toast({
        title: "Error",
        description: "Booking ID is missing.",
        variant: "destructive",
      });
    }

    cancelAppointment(data?.bookingId)
      .then(() => {
        toast({
          title: "Success",
          description: "Your appointment has been successfully canceled.",
          variant: "default",
        });
        navigate("/doctors");
      })
      .catch((error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      });
  }

  function handleViewPrescription(prescription: string) {
    if (!prescription)
      return toast({
        title: "Consolidated report not found",
        variant: "destructive",
      })

    const mimeType = prescription.split(",")[0].split(":")[1].split(";")[0]
    const blobUrl = base64PDFToBlobUrl(mimeType, prescription.split(",")[1])

    window.open(blobUrl)
  }

  function handleSelectSlot(slot: { from: string; to: string }) {
    //console.log("🎯 Selecting slot:", slot)
    setTempSelectedSlot(slot)
  }

  function handleOpenRescheduleModal() {
    //console.log("🔄 Opening reschedule modal");

    setSelectedBranch(branch?.branchId);

    // Parse current booking details from slotInfo
    const parsed = parseSlotInfo(slotInfo);

    if (parsed) {
      setSelectedDate(parsed.date);
      setCurrentBookingSlot(parsed.slot);
      //console.log("📅 Setting current booking.", parsed);
    } else {
      setSelectedDate(originalDate || today);
      setCurrentBookingSlot(null);
      // console.log("⚡ Fallback.");
    }

    setShowRescheduleModal(true);
  }

  function handleCancelReschedule() {
    //console.log("❌ Cancelling reschedule")
    setShowRescheduleModal(false)
    setTempSelectedSlot(null)
    setSelectedDate(today)
  }

  function handleConfirmReschedule() {

    if (!data?.bookingId || !tempSelectedSlot) {
      return toast({
        title: "Error",
        description: "Booking ID or selected slot is missing.",
        variant: "destructive",
      });
    }

    // Parse current booking from slotInfo for accurate comparison
    const currentBooking = parseSlotInfo(slotInfo);

    // Prevent confirming the same slot and date
    if (currentBooking &&
      selectedDate === currentBooking.date &&
      tempSelectedSlot.from === currentBooking.slot.from &&
      tempSelectedSlot.to === currentBooking.slot.to) {
      return toast({
        title: "No Change",
        description: "Please select a different date or slot to reschedule.",
        variant: "destructive",
      });
    }

    const [day, month, year] = selectedDate.split("-").map(Number);
    const jsDate = new Date(year, month - 1, day);

    const parseTime = (time: string) => {
      const [hourMinute, period] = time.split(" ");
      let [hours, minutes] = hourMinute.split(":").map(Number);
      if (period.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (period.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }
      return { hours, minutes };
    };

    const { hours: startHours, minutes: startMinutes } = parseTime(tempSelectedSlot.from);
    const { hours: endHours, minutes: endMinutes } = parseTime(tempSelectedSlot.to);

    const appointmentDate = jsDate.toISOString();
    const startTime = new Date(year, month - 1, day, startHours, startMinutes).toISOString();
    const endTime = new Date(year, month - 1, day, endHours, endMinutes).toISOString();

    const payload = {
      bookingId: data.bookingId,
      appointmentDate,
      startTime,
      endTime,
    };

    //console.log("📤 Sending reschedule payload:", payload);

    rescheduleAppointment(payload)
      .then(() => {
        //console.log("✅ Appointment rescheduled successfully");
        toast({
          title: "Success",
          description: "Your appointment has been successfully rescheduled.",
          variant: "default",
        });

        const formattedDate = format(jsDate, "d MMM yyyy");
        const newSlotInfo = `${formattedDate}, ${data?.dayOfWeek || "Unknown Day"}, ${tempSelectedSlot.from} - ${tempSelectedSlot.to}`;
        setSlotInfo(newSlotInfo);

        setTempSelectedSlot(null);
        setShowRescheduleModal(false);

        window.location.reload();
      })
      .catch((error) => {
        //console.error("❌ Error rescheduling appointment:", error);
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred while rescheduling.",
          variant: "destructive",
        });
      });
  }
  function normalizeTime(timeString: string): string {
    const trimmed = timeString.trim().toLowerCase();
    // Remove leading zero from hours (08:30 am -> 8:30 am)
    return trimmed.replace(/^0(\d):/, '$1:');
  }

  useEffect(() => {
    if (!isSlotsFetched || !showRescheduleModal) return;

    const allSlots = slots[selectedDate] ?? [];
    const now = new Date();

    const filtered = allSlots.filter(slot => {
      const [d, m, y] = selectedDate.split("-").map(Number);
      const [time, period] = slot.from.split(" ");
      const [h, min] = time.split(":").map(Number);

      const hour24 =
        period === "pm" && h !== 12 ? h + 12 :
          period === "am" && h === 12 ? 0 :
            h;

      const slotTime = new Date(y, m - 1, d, hour24, min);
      return slotTime >= now;
    });

    setCurrentActiveSlots(filtered);
    setPaginatedSlots(filtered.slice(0, MAX_SLOTS));

  }, [selectedDate, slots, isSlotsFetched, showRescheduleModal]);

  useEffect(() => {
    if (!showRescheduleModal || !currentBookingSlot) return;

    const currentBooking = parseSlotInfo(slotInfo);
    if (!currentBooking) return;

    if (selectedDate !== currentBooking.date) {
      setTempSelectedSlot(null);
      return;
    }

    const match = currentActiveSlots.find(s =>
      normalizeTime(s.from) === normalizeTime(currentBookingSlot.from) &&
      normalizeTime(s.to) === normalizeTime(currentBookingSlot.to)
    );

    if (match)
      setTempSelectedSlot(match);
  }, [showRescheduleModal, selectedDate, currentBookingSlot, currentActiveSlots]);

  useEffect(() => {
    if (!data?.bookingId) return;

    fetchPaymentConfirmDetails(data.bookingId)
      .then((res) => {
        if (res.paymentDone) {
          setPaymentChecked(true);
          setPaymentRef(res.upiIdOrMobileNumber || "");
        }
      })
      .catch(() => { });
  }, [data?.bookingId]);

  const submitPaymentConfirmation = () => {
    if (!data?.bookingId || !data?.patientCode) return;

    setLoadingPaymentSubmit(true);

    const payload = {
      bookingId: data.bookingId,
      patientId: Number(data?.patientCode),
      paymentDone: paymentChecked,
      upiIdOrMobileNumber: paymentRef,
    };

    patientConfirmsPayment(payload)
      .then(() => {
        toast({
          title: "Success",
          description: "Payment confirmation submitted successfully.",
        });
      })
      .catch((err) => {
        toast({
          title: "Error",
          description: err.message || "Unable to confirm payment",
          variant: "destructive",
        });
      })
      .finally(() => setLoadingPaymentSubmit(false));
  };

  async function onPaymentSubmit() {
    try {
      setLoadingPaymentSubmit(true);

      console.log("Data in onPaymentSubmit:", data);
      const payload = {
        bookingId: data?.bookingId!,
        patientId: Number(data?.patientCode!),   // ensure it’s number
        paymentDone: paymentChecked,
        upiIdOrMobileNumber: paymentRef,
      };

      await patientConfirmsPayment(payload);

      toast({
        title: "Payment Submitted",
        description: "Your payment details were sent successfully.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || "Failed to submit payment.",
        variant: "destructive",
      });
    } finally {
      setLoadingPaymentSubmit(false);
    }
  }

  return (
    <>
      <AppointmentHeader
        patientName={data?.patient?.name}
        appointmentType={data?.appointmentType}
      />

      <div className="container py-4 bg-gray-50 min-h-screen">
        {/* Doctor Details */}
        <DoctorDetailsCard doctor={doctor} />

        {slotInfo && (
          <AppointmentTimeBox
            slotInfo={slotInfo}
            onCancelClick={() => setIsModalOpen(true)}
            onRescheduleClick={handleOpenRescheduleModal}
          />
        )}

        <CancelAppointmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleCancelAppointment}
        />

        <RescheduleModal
          show={showRescheduleModal}
          slots={slots}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          slotInfo={slotInfo}
          tempSelectedSlot={tempSelectedSlot}
          currentActiveSlots={currentActiveSlots}
          paginatedSlots={paginatedSlots}
          handleSelectSlot={setTempSelectedSlot}
          handleCancel={handleCancelReschedule}
          handleConfirm={handleConfirmReschedule}
          MAX_SLOTS={MAX_SLOTS}
          setPaginatedSlots={setPaginatedSlots}
          parseSlotInfo={parseSlotInfo}
        />

        {data?.appointmentType === "TELECONSULTATION" && (
          <TeleConsultationSection
            data={data}
            paymentDetailsData={paymentDetailsData}
            copied={copied}
            setCopied={setCopied}
            handleViewPrescription={handleViewPrescription}
            paymentChecked={paymentChecked}
            setPaymentChecked={setPaymentChecked}
            paymentRef={paymentRef}
            setPaymentRef={setPaymentRef}
            loadingPaymentSubmit={loadingPaymentSubmit}
            onPaymentSubmit={onPaymentSubmit}
          />
        )}

        {/* {data?.status === "ACCEPTED" && (
          <PaymentConfirmation
            checked={paymentChecked}
            onCheckedChange={setPaymentChecked}
            paymentInfo={paymentRef}
            onPaymentInfoChange={setPaymentRef}
            loading={loadingPaymentSubmit}
            onSubmit={submitPaymentConfirmation}
          />
        )} */}

        <SymptomsCard data={data} />

        <ClinicDetailsCard branch={branch} />

        <HealthRecordsList
          healthRecords={healthRecords}
          loading={loading}
          handleViewPrescription={handleViewPrescription}
        />
      </div>
    </>
  )
}

export default AppointmentDetails